import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';

import {
    AppNotificationService,
    AppNotificationType,
    ConfigConstants,
    DateUtilities,
    ModalClass,
    NOTIFICATION_STATUS_CODE,
    SynchronizationService,
    TravelStatusUpdateInstanceToken,
    TravelStatusUpdateInterface,
    TravelStatusUpdateModel,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { EMPTY, from, merge, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, exhaustMap, take, takeUntil, tap } from 'rxjs/operators';
import moment from 'moment';
import { Router } from '@angular/router';

@Component({
    selector: 'pace-travel-status-update',
    templateUrl: './travel-status-update.component.html',
    styleUrls: ['./travel-status-update.component.scss'],
    standalone: false,
})
export class TravelStatusUpdateComponent implements OnInit, OnDestroy {
    @Input()
    public model: TravelStatusUpdateModel;

    @Output()
    public statusSubmitted = new EventEmitter<NOTIFICATION_STATUS_CODE>();

    public statusText: string;
    public isLoading = false;

    public formChildren: FormArray;
    public travelStatusForm: FormGroup;

    private submitStatusSubject: Subject<void>;
    private destroy$ = new Subject<void>();
    private submitStatusChanged$: Observable<void>;
    public isEarlyTravelRuleViolated = false;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        public readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
        @Inject(TravelStatusUpdateInstanceToken)
        private readonly travelStatusUpdateService: TravelStatusUpdateInterface,
        private readonly synchronizationService: SynchronizationService,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
        private readonly modalService: BsModalService,
        private readonly router: Router,
    ) {
        this.submitStatusSubject = new Subject<void>();
        this.submitStatusChanged$ = this.submitStatusSubject.asObservable();
    }

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.travelStatusForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });

        this.submitStatusChanged$
            .pipe(
                exhaustMap(() =>
                    this.travelStatusUpdateService.submitStatus(this.model).pipe(
                        concatMap((travelStatusModel) => {
                            // emit event that the travel status was updated to reload the status stepper button model
                            this.statusSubmitted.emit(travelStatusModel.nextStatus);
                            this.modalInstance.hide();

                            const navigate$ = this.model.isCurrentStatusDISP
                                ? from(this.router.navigate([ConfigConstants.routeUrlDashboard]))
                                : of(undefined);

                            const sync$ = this.sendOfflineData();

                            this.log.debug(
                                `TravelStatusUpdateComponent: submitStatus: sendOfflineData and route to dashboard`,
                            );
                            return merge(navigate$, sync$);
                        }),
                        tap({
                            error: (error) => {
                                this.log.debug(
                                    `TravelStatusUpdateComponent: submitStatus: error: ${JSON.stringify(
                                        error,
                                        Object.getOwnPropertyNames(error),
                                    )}`,
                                );
                                this.modalInstance.hide();
                            },
                        }),
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public submitStatus(): void {
        if (!this.handleEarlyTravelViolation()) {
            this.isLoading = true;
            this.triggerSubmitStatusChanged();
        }
    }

    public triggerSubmitStatusChanged(): void {
        this.submitStatusSubject.next();
    }

    // Synchronize the offline data
    private sendOfflineData(): Observable<void> {
        return this.synchronizationService.sendOfflineData().pipe(
            take(1),
            catchError((error) => {
                this.log.error(
                    `TravelStatusUpdateComponent: sendOfflineData: error:
                                ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                );
                this.appNotificationService.setAppNotification(
                    AppNotificationType.WARN,
                    <string>this.translate.instant('APP_SYNC_FAILED_TITLE'),
                    <string>this.translate.instant('APP_SYNC_FAILED_SYNCHRONIZATION'),
                );
                return EMPTY;
            }),
        );
    }

    public updateValidityAndEndMoment(): void {
        this.handleEarlyTravelViolation();

        this.formChildren.controls.forEach((control) => {
            control.updateValueAndValidity();
        });
        this.model.updateEndMoment();
    }

    private handleEarlyTravelViolation(): boolean {
        if (
            this.model.nextStatus !== NOTIFICATION_STATUS_CODE.OSR &&
            this.model.nextStatus !== NOTIFICATION_STATUS_CODE.TRAV
        ) {
            this.isEarlyTravelRuleViolated = false;
        } else {
            this.isEarlyTravelRuleViolated = this.travelStatusUpdateService.checkForEarlyTravelRuleViolation(
                this.model.notification.plannedStart,
                this.model.date.value,
            );
        }

        if (this.isEarlyTravelRuleViolated) {
            this.log.warn(`TravelStatusUpdateComponent: TRAV status update attempted earlier than allowed.`);
            this.showEarlyTravelHoursViolationModal(this.model.notification.plannedStart);
        }

        return this.isEarlyTravelRuleViolated;
    }

    private showEarlyTravelHoursViolationModal(plannedStart: moment.Moment): void {
        const messageBody = `${<string>this.translate.instant(
            'TRAVEL_STATUS_EARLY_TRAVEL_HOURS_RULE_VIOLATION_MODAL_BODY1',
            {
                earlyTravelHours: this.travelStatusUpdateService.earlyTravelHoursAllowed,
            },
        )}<br><br>${<string>this.translate.instant('TRAVEL_STATUS_EARLY_TRAVEL_HOURS_RULE_VIOLATION_MODAL_BODY2', {
            notificationId: this.model.notification.identifier.qmNum,
            plannedStartDate: DateUtilities.formatDate(plannedStart),
            plannedStartTime: DateUtilities.formatTime(plannedStart),
        })}`;

        this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    `${this.translate.instant('TRAVEL_STATUS_EARLY_TRAVEL_HOURS_RULE_VIOLATION_MODAL_TITLE')}`,
                    messageBody,
                    `${this.translate.instant('TRAVEL_STATUS_EARLY_TRAVEL_HOURS_RULE_VIOLATION_MODAL_BUTTON_OK')}`,
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });
    }
}
